import React, { useContext, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";

import { ZeosSpinner } from "../../../components/Shared/ZeosSpinner";
import type { IIncentiveCriterion } from "../../../components/Shared/ZeosTable/Interfaces";
import { ZeosApiContext } from "../../../context/ZeosApiContext";
import { formatExpectedBenefitRange } from "../../../mock-data/IncentivesTableData";
import { Analytics } from "../../../services/analytics/Analytics";
import { createLogger } from "../../../services/Logger";
import { getSearchParamNumber } from "../../../services/nav/QueryStringHelpers";
import { ICompanyClaims } from "../../../services/zeos-api/contracts/Account";
import { IIncentiveDetailContract } from "../../../services/zeos-api/contracts/Incentive";
import { DashboardFooter } from "../layout/DashboardFooter";
import { DashboardHeader } from "../layout/DashboardHeader";
import { extractCriteriaFromVectorJson } from "./IncentivesTable";

const currencyFormatter = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  maximumFractionDigits: 0,
});

const formatOptionalCurrency = (value?: number | null) => {
  if (typeof value !== "number" || !Number.isFinite(value)) {
    return null;
  }
  return currencyFormatter.format(value);
};

const formatDeadline = (endDate?: string | null) => {
  if (!endDate || endDate === "1/1/0001" || endDate === "01/01/0001") {
    return "Open";
  }
  return endDate;
};

export const IncentiveDetail = () => {
  const api = useContext(ZeosApiContext);
  const navigate = useNavigate();
  const log = createLogger("IncentiveDetail");
  const { incentiveId: incentiveIdParam } = useParams();
  const [searchParams] = useSearchParams();
  const propertyId = getSearchParamNumber("property", searchParams);
  const screeningRunGuid = searchParams.get("screeningRun");
  const incentiveId = Number(incentiveIdParam);

  const [activeCompany, setActiveCompany] = useState<ICompanyClaims>();
  const [detail, setDetail] = useState<IIncentiveDetailContract | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let isMounted = true;

    const loadDetailAsync = async () => {
      if (!Number.isFinite(incentiveId) || incentiveId <= 0) {
        setError("Invalid incentive.");
        setLoading(false);
        return;
      }

      const hasProperty = Boolean(propertyId);
      const hasScreening = Boolean(screeningRunGuid);
      if (hasProperty === hasScreening) {
        setError("Select a property or screening run to view this incentive.");
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const response = await api.Incentive.getIncentiveDetailById(incentiveId, {
          propertyId,
          screeningRunGuid,
        });
        if (!isMounted) {
          return;
        }

        if (response.zeosStatus?.toLowerCase() !== "success" || !response.data) {
          throw new Error(response.message || "Unable to load incentive detail.");
        }

        setDetail(response.data);
        Analytics.capture("incentive_match_viewed", {
          incentive_id: incentiveId,
          source: hasProperty ? "property" : "screening",
        });
      } catch (err: any) {
        if (!isMounted) {
          return;
        }
        log.error("Failed to load incentive detail", err);
        setDetail(null);
        setError(err?.message || "Unable to load incentive detail.");
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    loadDetailAsync();
    return () => {
      isMounted = false;
    };
  }, [api.Incentive, incentiveId, propertyId, screeningRunGuid]);

  const criteria = useMemo(() => {
    const parsed = extractCriteriaFromVectorJson(detail?.vectorJson);
    if (parsed.length > 0) {
      return parsed;
    }
    if (detail?.isManualLink) {
      return [
        {
          criterion: "Manual link",
          result: true,
          reason: "Added manually by an admin.",
        } as IIncentiveCriterion,
      ];
    }
    return [] as IIncentiveCriterion[];
  }, [detail]);

  const matchingCriteria = useMemo(() => {
    const eligible = criteria.filter((item) => item.result === true);
    return eligible.length > 0 ? eligible : criteria;
  }, [criteria]);

  const backToListHref = screeningRunGuid
    ? `/incentives?screeningRun=${encodeURIComponent(screeningRunGuid)}`
    : propertyId
    ? `/incentives?property=${propertyId}`
    : "/incentives";

  const economicFacts = [
    {
      label: "Max incentive amount",
      value: formatOptionalCurrency(detail?.maxIncentiveAmount),
    },
    {
      label: "Incentive rate",
      value:
        detail?.incentiveRate != null
          ? `${detail.incentiveRate}${
              detail.incentiveRateUnit ? ` ${detail.incentiveRateUnit}` : ""
            }`
          : null,
    },
    {
      label: "Cost share",
      value:
        detail?.costSharePercent != null
          ? `${detail.costSharePercent}%`
          : null,
    },
  ].filter((item) => Boolean(item.value));

  return (
    <div className="flex flex-col min-h-screen">
      <DashboardHeader companyCallback={setActiveCompany} />
      <main className="bg-zeos-white-md p-4 flex-grow">
        <div className="mx-auto max-w-5xl font-sans">
          <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
            <button
              type="button"
              onClick={() => navigate(backToListHref)}
              className="text-sm text-zeos-orange-dk hover:underline"
            >
              ← Back to Incentives
            </button>
            {activeCompany?.companyName ? (
              <div className="text-xs text-slate-500">{activeCompany.companyName}</div>
            ) : null}
          </div>

          {loading ? (
            <ZeosSpinner />
          ) : error ? (
            <div className="rounded border border-red-200 bg-red-50 p-4 text-sm text-red-700">
              {error}
            </div>
          ) : detail ? (
            <div className="space-y-6">
              <section className="border border-gray-200 bg-white p-6 shadow-sm">
                <div className="text-xs uppercase tracking-wide text-slate-500">
                  {[detail.category, detail.area, detail.type]
                    .filter(Boolean)
                    .join(" · ")}
                </div>
                <h1 className="mt-2 text-2xl font-semibold text-zeos-black">
                  {detail.name}
                  {detail.isManualLink ? (
                    <span className="ml-2 text-sm font-medium text-slate-500">
                      (Manual)
                    </span>
                  ) : null}
                </h1>
                {detail.programName && detail.programName !== detail.name ? (
                  <p className="mt-1 text-sm text-slate-600">
                    Program: {detail.programName}
                  </p>
                ) : null}
                <div className="mt-4 grid gap-3 text-sm text-slate-700 sm:grid-cols-2">
                  <div>
                    <span className="font-semibold text-slate-900">Provider:</span>{" "}
                    {detail.rationale?.provider ||
                      detail.aiSummary?.providerName ||
                      detail.partnerName ||
                      detail.provider ||
                      "—"}
                  </div>
                  <div>
                    <span className="font-semibold text-slate-900">Deadline:</span>{" "}
                    {formatDeadline(detail.endDate)}
                  </div>
                  <div>
                    <span className="font-semibold text-slate-900">Status:</span>{" "}
                    {detail.programStatus || "—"}
                  </div>
                  <div>
                    <span className="font-semibold text-slate-900">
                      Program website:
                    </span>{" "}
                    {detail.programUrl ? (
                      <a
                        href={detail.programUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-zeos-orange-dk hover:underline"
                      >
                        Open program site
                      </a>
                    ) : (
                      "—"
                    )}
                  </div>
                </div>
                {detail.description ? (
                  <p className="mt-4 whitespace-pre-wrap text-sm leading-relaxed text-slate-700">
                    {detail.description}
                  </p>
                ) : null}
                <div className="mt-6">
                  <button
                    type="button"
                    onClick={() => undefined}
                    className="rounded bg-zeos-orange-dk px-4 py-2 text-sm font-semibold text-white hover:opacity-90"
                  >
                    Add to Roadmap
                  </button>
                </div>
              </section>

              <section className="border border-gray-200 bg-white p-6 shadow-sm">
                <h2 className="text-lg font-semibold text-zeos-black">
                  Why this incentive
                </h2>
                {(() => {
                  const aiState = (detail.aiAnalysisState || "none").toLowerCase();
                  if (aiState !== "ready") {
                    return (
                      <p className="mt-3 text-sm text-slate-600">
                        {aiState === "pending"
                          ? "AI analysis is being prepared — refresh this page in a moment."
                          : "AI analysis not available."}
                      </p>
                    );
                  }

                  const rationale = detail.rationale;
                  const criteriaMet =
                    rationale?.eligibilityCriteriaMet?.filter(Boolean) ??
                    detail.aiSummary?.eligibilityCriteriaMet?.filter(Boolean) ??
                    [];
                  const valueBasis =
                    rationale?.valueBasis ||
                    detail.aiSummary?.benefitRange?.basis ||
                    null;
                  const suitability =
                    rationale?.suitability ||
                    (detail.aiSummary?.fitAssessment?.suitability
                      ? detail.aiSummary.fitAssessment.suitability
                          .charAt(0)
                          .toUpperCase() +
                        detail.aiSummary.fitAssessment.suitability.slice(1).toLowerCase()
                      : null);
                  const fitReasons =
                    rationale?.reasons?.filter(Boolean) ??
                    detail.aiSummary?.fitAssessment?.reasons?.filter(Boolean) ??
                    [];
                  const provider =
                    rationale?.provider ||
                    detail.aiSummary?.providerName ||
                    detail.partnerName ||
                    detail.provider ||
                    null;
                  const timing =
                    rationale?.applicationTiming ||
                    detail.aiSummary?.applicationTiming ||
                    null;
                  const hasRationaleBlock =
                    criteriaMet.length > 0 ||
                    Boolean(valueBasis) ||
                    Boolean(suitability) ||
                    fitReasons.length > 0 ||
                    Boolean(provider) ||
                    Boolean(timing);

                  return (
                    <>
                      {hasRationaleBlock ? (
                        <div className="mt-4 space-y-3 rounded border border-gray-100 bg-zeos-white-md p-4 text-sm text-slate-700">
                          <h3 className="text-sm font-semibold uppercase tracking-wide text-slate-500">
                            Match rationale
                          </h3>
                          <div>
                            <div className="font-semibold text-zeos-black">
                              Eligibility criteria met
                            </div>
                            {criteriaMet.length > 0 ? (
                              <ul className="mt-1 list-disc space-y-1 pl-5">
                                {criteriaMet.map((item) => (
                                  <li key={item}>{item}</li>
                                ))}
                              </ul>
                            ) : (
                              <p className="mt-1 text-slate-500">Not specified</p>
                            )}
                          </div>
                          <div>
                            <div className="font-semibold text-zeos-black">
                              Value basis
                            </div>
                            <p className="mt-1">{valueBasis || "Not specified"}</p>
                          </div>
                          <div>
                            <div className="font-semibold text-zeos-black">
                              Suitability
                            </div>
                            <p className="mt-1">{suitability || "Not specified"}</p>
                          </div>
                          <div>
                            <div className="font-semibold text-zeos-black">
                              Reasons
                            </div>
                            {fitReasons.length > 0 ? (
                              <ul className="mt-1 list-disc space-y-1 pl-5">
                                {fitReasons.map((item) => (
                                  <li key={item}>{item}</li>
                                ))}
                              </ul>
                            ) : (
                              <p className="mt-1 text-slate-500">Not specified</p>
                            )}
                          </div>
                          <div>
                            <div className="font-semibold text-zeos-black">
                              Provider
                            </div>
                            <p className="mt-1">{provider || "Not specified"}</p>
                          </div>
                          <div>
                            <div className="font-semibold text-zeos-black">
                              Application timing
                            </div>
                            <p className="mt-1">{timing || "Not specified"}</p>
                          </div>
                        </div>
                      ) : null}
                      {detail.aiSummary?.shortText ? (
                        <p className="mt-3 text-sm text-slate-700">
                          {detail.aiSummary.shortText}
                        </p>
                      ) : null}
                      {detail.aiSummary?.longText ? (
                        <p className="mt-3 text-sm leading-relaxed text-slate-700">
                          {detail.aiSummary.longText}
                        </p>
                      ) : null}
                      {detail.aiSummary?.bullets && detail.aiSummary.bullets.length > 0 ? (
                        <ul className="mt-3 list-disc space-y-1 pl-5 text-sm text-slate-700">
                          {detail.aiSummary.bullets.map((bullet) => (
                            <li key={bullet}>{bullet}</li>
                          ))}
                        </ul>
                      ) : null}
                      {!detail.aiSummary?.shortText &&
                      !detail.aiSummary?.longText &&
                      !(detail.aiSummary?.bullets && detail.aiSummary.bullets.length > 0) ? (
                        <p className="mt-3 text-sm text-slate-500">
                          No AI summary is available for this property and incentive yet.
                        </p>
                      ) : null}
                    </>
                  );
                })()}

                <h3 className="mt-6 text-sm font-semibold uppercase tracking-wide text-slate-500">
                  Matching criteria
                </h3>
                {matchingCriteria.length === 0 ? (
                  <p className="mt-2 text-sm text-slate-500">
                    No matching criteria were recorded for this incentive.
                  </p>
                ) : (
                  <ul className="mt-3 space-y-3">
                    {matchingCriteria.map((item) => (
                      <li
                        key={`${item.criterion}-${item.reason}`}
                        className="rounded border border-gray-100 bg-zeos-white-md p-3 text-sm"
                      >
                        <div className="font-semibold text-zeos-black">
                          {item.criterion || "Criterion"}
                        </div>
                        {item.reason ? (
                          <div className="mt-1 text-slate-700">{item.reason}</div>
                        ) : null}
                      </li>
                    ))}
                  </ul>
                )}
              </section>

              <section className="border border-gray-200 bg-white p-6 shadow-sm">
                <h2 className="text-lg font-semibold text-zeos-black">
                  Economic benefits
                </h2>
                {(() => {
                  const aiState = (detail.aiAnalysisState || "none").toLowerCase();
                  if (aiState !== "ready") {
                    return (
                      <p className="mt-3 text-sm text-slate-600">
                        {aiState === "pending"
                          ? "AI analysis is being prepared — refresh this page in a moment."
                          : "AI analysis not available."}
                      </p>
                    );
                  }

                  return (
                    <>
                      <p className="mt-3 text-base font-semibold text-zeos-black">
                        {formatExpectedBenefitRange(detail.aiSummary)}
                      </p>
                      {detail.aiSummary?.benefitRange?.basis ? (
                        <p className="mt-2 text-sm text-slate-600">
                          {detail.aiSummary.benefitRange.basis}
                        </p>
                      ) : null}
                      {detail.aiSummary?.benefitRange?.confidence ? (
                        <p className="mt-1 text-xs text-slate-500">
                          Confidence: {detail.aiSummary.benefitRange.confidence}
                        </p>
                      ) : null}
                    </>
                  );
                })()}
                {economicFacts.length > 0 ? (
                  <dl className="mt-4 grid gap-3 sm:grid-cols-3">
                    {economicFacts.map((fact) => (
                      <div
                        key={fact.label}
                        className="rounded border border-gray-100 bg-zeos-white-md p-3"
                      >
                        <dt className="text-xs uppercase tracking-wide text-slate-500">
                          {fact.label}
                        </dt>
                        <dd className="mt-1 text-sm font-semibold text-zeos-black">
                          {fact.value}
                        </dd>
                      </div>
                    ))}
                  </dl>
                ) : null}
              </section>
            </div>
          ) : null}
        </div>
      </main>
      <DashboardFooter />
    </div>
  );
};
